/* Client half of the rolling-window rate limits enforced by firestore.rules (see `limiterBumped`
 * / `limiterWriteOk` there and src/domain/rateLimit.ts for the pure decision logic). A
 * rate-limited create must be committed in the same write as a counter update; this file reads the
 * counter, turns a decision into the exact counter write the rules accept, and retries once with
 * the opposite branch when the device clock disagrees with the server about whether the window has
 * elapsed. */
import { serverTimestamp, type DocumentSnapshot, type FieldValue, type Timestamp } from 'firebase/firestore'
import { decideLimiter, retryAfterSeconds, type LimiterDecision, type LimiterState } from '@/domain/rateLimit'

export type LimiterForce = 'new-window' | 'increment'

interface LimiterDoc {
  windowStartAt?: Timestamp
  count?: number
}

/** The caller's current window, or null when the counter is missing or legacy-shaped (no
 *  `windowStartAt`) — the rules treat both as an expired window too. */
export function readLimiter(snap: DocumentSnapshot): { state: LimiterState; startedAt: Timestamp } | null {
  if (!snap.exists()) return null
  const d = snap.data() as LimiterDoc
  if (!d.windowStartAt || typeof d.count !== 'number') return null
  return { state: { windowStartMs: d.windowStartAt.toMillis(), count: d.count }, startedAt: d.windowStartAt }
}

/** Decide against the counter and return the exact doc to write, or throw a friendly error when
 *  the cap is reached. `record` reports which branch was chosen so a caller can retry the other. */
export function planLimiterWrite(
  snap: DocumentSnapshot,
  nowMs: number,
  max: number,
  noun: string,
  force: LimiterForce | undefined,
  record: (kind: 'new-window' | 'increment') => void,
): { windowStartAt: FieldValue | Timestamp; count: number } {
  const prev = readLimiter(snap)
  const decision: LimiterDecision = decideLimiter(prev?.state ?? null, nowMs, max, force)
  if (decision.kind === 'blocked') {
    throw new Error(`Too many ${noun} — please wait ${retryAfterSeconds(decision.retryAfterMs)}s and try again.`)
  }
  record(decision.kind)
  if (decision.kind === 'new-window' || !prev) return { windowStartAt: serverTimestamp(), count: 1 }
  return { windowStartAt: prev.startedAt, count: decision.count }
}

function isPermissionDenied(e: unknown): boolean {
  return (e as { code?: string } | null)?.code === 'permission-denied'
}

/** Runs a rate-limited transaction. If it's rejected with permission-denied — which for these
 *  writes usually means the client and server disagreed on whether the 60s window had elapsed —
 *  it retries ONCE with the opposite branch, then surfaces the original error if that also fails
 *  (a genuine denial: blocked, inactive, over the cap, etc.). */
export async function runLimited<T>(
  run: (force: LimiterForce | undefined, record: (kind: 'new-window' | 'increment') => void) => Promise<T>,
): Promise<T> {
  let first: 'new-window' | 'increment' | undefined
  try {
    return await run(undefined, (k) => {
      first = k
    })
  } catch (e) {
    if (!isPermissionDenied(e) || !first) throw e
    try {
      return await run(first === 'increment' ? 'new-window' : 'increment', () => {})
    } catch {
      throw e
    }
  }
}
